import { ExpressionValueType } from "../../../rule-engine-core/Expressions/ExpressionValueType";
import { ExistsExpression } from "../../../rule-engine-core/Expressions/Expressions/Logic/Exists";
import {
    IfThenExpression,
    IfThenPair,
    IfThenStyle,
} from "../../../rule-engine-core/Expressions/Expressions/Logic/IfThenExpression";
import { StringEqualExpression } from "../../../rule-engine-core/Expressions/Expressions/String/StringEqualExpression";
import { ExpressionRule } from "../../../rule-engine-core/Expressions/ExpressionRule";
import { StatementRuleConfiguration } from "../../../rule-engine-core/Rule/Statement/StatementRuleConfiguration";
import type {
    RuleIntegrationEvaluationContext,
    RuleSimplifiedLogicEdit,
    RuleSimplifiedLogicEditorModel,
    RuleSimplifiedLogicEditorPort,
    RuleSimplifiedLogicFieldOption,
    RuleSummaryAst,
    RuleSummaryPresentation,
    RuleSummaryPresenterPort,
    RuleSummaryValue,
} from "./ruleIntegrationTypes";
import { createRuleSummaryPresentation } from "./ruleIntegrationTypes";
import { summarizeIfThenExpression } from "./appglueExpressionSummary";

type StringOption = RuleSimplifiedLogicFieldOption;

type RequiredFieldsState = {
    conditionValue: string;
    requiredFields: string[];
};

type BlockedValuesState = {
    field: string;
    values: string[];
};

type StatementRuleCharacterization<State> =
    | {
          status: "complete";
          state: State;
          warnings: string[];
      }
    | {
          status: "unsupported";
          state: null;
          warnings: string[];
      };

export type RequiredFieldsBridgeConfig = {
    mode: "required_fields_for_condition";
    conditionId: string;
    requirementId: string;
    conditionField: string;
    conditionValueOptions: StringOption[];
    conditionValueInputLabel: string;
    requiredFieldOptions: StringOption[];
    requiredFieldInputLabel: string;
    defaultConditionValue: string;
    defaultRequiredFields: string[];
    actionFor: (state: RequiredFieldsState, context?: RuleIntegrationEvaluationContext) => string;
};

export type BlockedValuesBridgeConfig = {
    mode: "blocked_values";
    conditionId: string;
    conditionFieldOptions: StringOption[];
    conditionValueOptions: StringOption[];
    conditionValueInputLabel: string;
    defaultField: string;
    defaultValues: string[];
    actionFor: (state: BlockedValuesState, context?: RuleIntegrationEvaluationContext) => string;
};

export type StatementRuleSimplifiedLogicBridgeConfig = RequiredFieldsBridgeConfig | BlockedValuesBridgeConfig;

export type StatementRuleSimplifiedLogicBridge = {
    summaryPresenter: RuleSummaryPresenterPort<StatementRuleConfiguration>;
    simplifiedLogicEditor: RuleSimplifiedLogicEditorPort<StatementRuleConfiguration>;
};

export const createStringEqualExpression = (field: string, value: string): StringEqualExpression => {
    const expression = new StringEqualExpression();
    expression.value1.variableName = field;
    expression.value2.value = value;
    return expression;
};

export const createExistsExpression = (field: string): ExistsExpression => {
    const expression = new ExistsExpression();
    expression.dataPath = field;
    return expression;
};

export const setStatementRuleBranchExpressions = (branch: IfThenPair, expressions: ExpressionRule[]) => {
    branch.ifExpression.elements = [];
    expressions.forEach((expression) => {
        branch.ifExpression.add();
        branch.ifExpression.elements[branch.ifExpression.elements.length - 1]!.subExpression = expression;
    });
};

const optionValues = (options: StringOption[]): string[] => options.map((option) => option.value);

const normalizeWithOptions = (values: string[], options: StringOption[]): string[] => {
    const optionsByValue = new Map(options.map((option) => [option.value.toLowerCase(), option.value]));
    const optionsByLabel = new Map(options.map((option) => [option.label.toLowerCase(), option.value]));
    const normalized = values
        .map((value) => {
            const key = value.trim().toLowerCase();
            return optionsByValue.get(key) ?? optionsByLabel.get(key);
        })
        .filter((value): value is string => !!value);

    return Array.from(new Set(normalized));
};

const getStringEquals = (branch: IfThenPair): StringEqualExpression[] =>
    branch.ifExpression.elements
        .map((element) => element.subExpression)
        .filter((expressionRule): expressionRule is StringEqualExpression =>
            expressionRule instanceof StringEqualExpression,
        );

const getSingleStringEquals = (branch: IfThenPair): StringEqualExpression | null => {
    const expressions = getStringEquals(branch);
    return expressions.length === 1 ? expressions[0]! : null;
};

const getStringEqualsValue = (branch: IfThenPair, field: string): string | null => {
    const expression = getStringEquals(branch).find((candidate) => candidate.value1.variableName === field);
    return typeof expression?.value2.value === "string" ? expression.value2.value : null;
};

const expressionLabel = (expressionRule: ExpressionRule | undefined): string =>
    expressionRule?.__type ?? "blank expression value";

const getBranchRules = (branch: IfThenPair): Array<ExpressionRule | undefined> =>
    branch.ifExpression.elements.map((element) => element.subExpression);

const isStringEqualsFor = (
    expressionRule: ExpressionRule | undefined,
    field: string,
    value?: string,
): expressionRule is StringEqualExpression =>
    expressionRule instanceof StringEqualExpression &&
    expressionRule.value1.variableName === field &&
    (value === undefined || expressionRule.value2.value === value);

const isAllowedExistsExpression = (
    expressionRule: ExpressionRule | undefined,
    allowedFields: Set<string>,
): expressionRule is ExistsExpression =>
    expressionRule instanceof ExistsExpression &&
    typeof expressionRule.dataPath === "string" &&
    allowedFields.has(expressionRule.dataPath);

const unsupportedCharacterization = <State>(message: string): StatementRuleCharacterization<State> => ({
    status: "unsupported",
    state: null,
    warnings: [message],
});

const characterizeRequiredFieldsCondition = (
    config: StatementRuleConfiguration,
    bridgeConfig: RequiredFieldsBridgeConfig,
): StatementRuleCharacterization<RequiredFieldsState> => {
    const expression = config.expression.expression;
    if (!(expression instanceof IfThenExpression)) {
        return unsupportedCharacterization("Simplified summary supports IfThenExpression statement rules only.");
    }

    const completeBranch = expression.ifBranches[0];
    const incompleteBranch = expression.ifBranches[1];
    if (!completeBranch || !incompleteBranch) {
        return unsupportedCharacterization(
            "Required-field summary expects a complete branch and an incomplete branch.",
        );
    }

    if (expression.ifBranches.length !== 2) {
        return unsupportedCharacterization(
            `Required-field summary expects exactly 2 branches; found ${expression.ifBranches.length}.`,
        );
    }

    if (
        completeBranch.thenExpression.value !== true ||
        incompleteBranch.thenExpression.value !== false ||
        expression.defaultOutput.value !== true
    ) {
        return unsupportedCharacterization(
            "Required-field summary only supports true-on-complete, false-on-incomplete, true-default expressions.",
        );
    }

    const conditionValue = getStringEqualsValue(completeBranch, bridgeConfig.conditionField);
    if (!conditionValue) {
        return unsupportedCharacterization(
            `Required-field summary could not find ${bridgeConfig.conditionField} equality in the complete branch.`,
        );
    }

    const allowedFields = new Set(optionValues(bridgeConfig.requiredFieldOptions));
    const completeBranchRules = getBranchRules(completeBranch);
    const completeConditionRules = completeBranchRules.filter((expressionRule) =>
        isStringEqualsFor(expressionRule, bridgeConfig.conditionField, conditionValue),
    );
    if (completeConditionRules.length !== 1) {
        return unsupportedCharacterization(
            "Required-field summary expects one category equality in the complete branch.",
        );
    }

    const unsupportedCompleteRules = completeBranchRules.filter(
        (expressionRule) =>
            !isStringEqualsFor(expressionRule, bridgeConfig.conditionField, conditionValue) &&
            !isAllowedExistsExpression(expressionRule, allowedFields),
    );
    if (unsupportedCompleteRules.length > 0) {
        return unsupportedCharacterization(
            `Required-field summary cannot cover complete-branch clauses: ${unsupportedCompleteRules
                .map(expressionLabel)
                .join(", ")}.`,
        );
    }

    const incompleteBranchRules = getBranchRules(incompleteBranch);
    if (
        incompleteBranchRules.length !== 1 ||
        !isStringEqualsFor(incompleteBranchRules[0], bridgeConfig.conditionField, conditionValue)
    ) {
        return unsupportedCharacterization(
            "Required-field summary expects the incomplete branch to contain only the same category equality.",
        );
    }

    const requiredFields = completeBranchRules
        .filter((expressionRule): expressionRule is ExistsExpression =>
            isAllowedExistsExpression(expressionRule, allowedFields),
        )
        .map((expressionRule) => expressionRule.dataPath)
        .filter((field): field is string => typeof field === "string" && allowedFields.has(field));

    return {
        status: "complete",
        state: {
            conditionValue,
            requiredFields,
        },
        warnings: [],
    };
};

export const getRequiredFieldsCondition = (
    config: StatementRuleConfiguration,
    bridgeConfig: RequiredFieldsBridgeConfig,
): RequiredFieldsState | null => {
    const characterization = characterizeRequiredFieldsCondition(config, bridgeConfig);
    return characterization.status === "complete" ? characterization.state : null;
};

export const buildRequiredFieldsExpression = (
    bridgeConfig: RequiredFieldsBridgeConfig,
    state: RequiredFieldsState,
): IfThenExpression => {
    const expression = new IfThenExpression();
    expression.ifThenStyle = IfThenStyle.BRANCH_DATA_RETURN;

    const completeBranch = expression.ifBranches[0]!;
    setStatementRuleBranchExpressions(completeBranch, [
        createStringEqualExpression(bridgeConfig.conditionField, state.conditionValue),
        ...state.requiredFields.map(createExistsExpression),
    ]);
    completeBranch.thenExpression.value = true;

    const incompleteBranch = expression.addIfBranch();
    setStatementRuleBranchExpressions(incompleteBranch, [
        createStringEqualExpression(bridgeConfig.conditionField, state.conditionValue),
    ]);
    incompleteBranch.thenExpression.value = false;

    expression.defaultOutput.value = true;

    return expression;
};

const characterizeBlockedValuesCondition = (
    config: StatementRuleConfiguration,
    bridgeConfig: BlockedValuesBridgeConfig,
): StatementRuleCharacterization<BlockedValuesState> => {
    const expression = config.expression.expression;
    if (!(expression instanceof IfThenExpression)) {
        return unsupportedCharacterization("Simplified summary supports IfThenExpression statement rules only.");
    }

    if (expression.defaultOutput.value !== true) {
        return unsupportedCharacterization("Blocked-value summary only supports true default output expressions.");
    }

    const falseBranches = expression.ifBranches.filter((branch) => branch.thenExpression.value === false);
    if (falseBranches.length !== expression.ifBranches.length) {
        return unsupportedCharacterization(
            "Blocked-value summary expects every explicit branch to return false.",
        );
    }
    if (falseBranches.length === 0) {
        return {
            status: "complete",
            state: { field: bridgeConfig.defaultField, values: [] },
            warnings: [],
        };
    }

    const allowedFields = new Set(optionValues(bridgeConfig.conditionFieldOptions));
    const allowedValues = new Set(optionValues(bridgeConfig.conditionValueOptions));
    const expressions = falseBranches.map(getSingleStringEquals);
    if (expressions.some((expressionRule) => !expressionRule)) {
        return unsupportedCharacterization(
            "Blocked-value summary expects each branch to contain one string equality.",
        );
    }

    const field = expressions[0]!.value1.variableName;
    if (!field || !allowedFields.has(field)) {
        return unsupportedCharacterization("Blocked-value summary found an unsupported field.");
    }
    if (expressions.some((expressionRule) => expressionRule!.value1.variableName !== field)) {
        return unsupportedCharacterization("Blocked-value summary expects every branch to use the same field.");
    }

    const values = expressions
        .map((expressionRule) => expressionRule!.value2.value)
        .filter((value): value is string => typeof value === "string" && allowedValues.has(value));
    if (values.length !== expressions.length) {
        return unsupportedCharacterization("Blocked-value summary found an unsupported comparison value.");
    }

    return {
        status: "complete",
        state: {
            field,
            values: Array.from(new Set(values)),
        },
        warnings: [],
    };
};

export const getBlockedValuesCondition = (
    config: StatementRuleConfiguration,
    bridgeConfig: BlockedValuesBridgeConfig,
): BlockedValuesState | null => {
    const characterization = characterizeBlockedValuesCondition(config, bridgeConfig);
    return characterization.status === "complete" ? characterization.state : null;
};

export const buildBlockedValuesExpression = (
    state: BlockedValuesState,
): IfThenExpression => {
    const expression = new IfThenExpression();
    expression.ifThenStyle = IfThenStyle.BRANCH_DATA_RETURN;

    state.values.forEach((value, index) => {
        const branch = index === 0 ? expression.ifBranches[0]! : expression.addIfBranch();
        setStatementRuleBranchExpressions(branch, [createStringEqualExpression(state.field, value)]);
        branch.thenExpression.value = false;
    });

    expression.defaultOutput.value = true;

    return expression;
};

const buildRequiredFieldsSummaryAst = (
    state: RequiredFieldsState,
    bridgeConfig: RequiredFieldsBridgeConfig,
    context?: RuleIntegrationEvaluationContext,
): RuleSummaryAst => ({
    kind: "rule_summary",
    statements: [
        {
            kind: "condition",
            keyword: "IF",
            expression: {
                kind: "comparison",
                left: { kind: "field", field: bridgeConfig.conditionField },
                operator: "==",
                right: { kind: "literal", value: state.conditionValue },
            },
            editPort: {
                conditionId: bridgeConfig.conditionId,
                canEditValues: true,
            },
        },
        {
            kind: "requirement",
            fields: state.requiredFields,
            editPort: {
                requirementId: bridgeConfig.requirementId,
                canEditValues: true,
            },
        },
        {
            kind: "action",
            label: bridgeConfig.actionFor(state, context),
        },
    ],
});

const buildBlockedValuesSummaryAst = (
    state: BlockedValuesState,
    bridgeConfig: BlockedValuesBridgeConfig,
    context?: RuleIntegrationEvaluationContext,
): RuleSummaryAst => ({
    kind: "rule_summary",
    statements: [
        state.values.length > 0
            ? {
                  kind: "condition",
                  keyword: "IF",
                  expression:
                      state.values.length === 1
                          ? {
                                kind: "comparison",
                                left: { kind: "field", field: state.field },
                                operator: "==",
                                right: { kind: "literal", value: state.values[0] },
                            }
                          : {
                                kind: "in_list",
                                left: { kind: "field", field: state.field },
                                values: state.values,
                            },
                  editPort: {
                      conditionId: bridgeConfig.conditionId,
                      canEditField: true,
                      canEditValues: true,
                  },
              }
            : {
                  kind: "text",
                  text: "IF no lifecycle branches",
              },
        {
            kind: "action",
            label: bridgeConfig.actionFor(state, context),
        },
    ],
});

const defaultActionLabel = (
    bridgeConfig: StatementRuleSimplifiedLogicBridgeConfig,
    context?: RuleIntegrationEvaluationContext,
): string => {
    if (bridgeConfig.mode === "required_fields_for_condition") {
        return bridgeConfig.actionFor(
            {
                conditionValue: bridgeConfig.defaultConditionValue,
                requiredFields: bridgeConfig.defaultRequiredFields,
            },
            context,
        );
    }

    return bridgeConfig.actionFor(
        {
            field: bridgeConfig.defaultField,
            values: bridgeConfig.defaultValues,
        },
        context,
    );
};

const unsupportedNativeSummary = (
    config: StatementRuleConfiguration,
    warnings: string[],
    bridgeConfig: StatementRuleSimplifiedLogicBridgeConfig,
    context?: RuleIntegrationEvaluationContext,
): RuleSummaryPresentation => {
    const expression = config.expression.expression;
    if (expression instanceof IfThenExpression) {
        const nativeSummary = summarizeIfThenExpression(expression);
        return createRuleSummaryPresentation({
            status: "unsupported",
            ast: {
                kind: "rule_summary",
                statements: [
                    ...nativeSummary.ast.statements,
                    {
                        kind: "action",
                        label: defaultActionLabel(bridgeConfig, context),
                    },
                ],
            },
            sources: ["native_appglue_expression", "adapter_routing"],
            warnings: [...warnings, ...nativeSummary.warnings],
        });
    }

    return createRuleSummaryPresentation({
        status: "unsupported",
        lines: ["APPGLUE native expression requires the native editor for full logic"],
        sources: ["native_appglue_expression"],
        warnings,
    });
};

const resultValueSummary = (value: unknown): RuleSummaryPresentation =>
    createRuleSummaryPresentation({
        status: "complete",
        ast: {
            kind: "rule_summary",
            statements: [{ kind: "result", value: value as RuleSummaryValue }],
        },
        sources: ["native_appglue_expression"],
    });

const getEditorModel = (
    config: StatementRuleConfiguration,
    bridgeConfig: StatementRuleSimplifiedLogicBridgeConfig,
): RuleSimplifiedLogicEditorModel | null => {
    if (bridgeConfig.mode === "required_fields_for_condition") {
        const characterization = characterizeRequiredFieldsCondition(config, bridgeConfig);
        if (characterization.status !== "complete") return null;
        const state = characterization.state;
        if (state.requiredFields.length === 0) return null;

        return {
            condition: {
                id: bridgeConfig.conditionId,
                keyword: "IF",
                field: bridgeConfig.conditionField,
                fieldOptions: [{ value: bridgeConfig.conditionField, label: bridgeConfig.conditionField }],
                operatorLabel: "==",
                values: [state.conditionValue],
                valueOptions: bridgeConfig.conditionValueOptions,
                valueInputLabel: bridgeConfig.conditionValueInputLabel,
            },
            requirement: {
                id: bridgeConfig.requirementId,
                keyword: "REQUIRE",
                values: state.requiredFields,
                valueOptions: bridgeConfig.requiredFieldOptions,
                valueInputLabel: bridgeConfig.requiredFieldInputLabel,
            },
        };
    }

    const characterization = characterizeBlockedValuesCondition(config, bridgeConfig);
    if (characterization.status !== "complete") return null;
    const state = characterization.state;
    if (state.values.length === 0) return null;

    return {
        condition: {
            id: bridgeConfig.conditionId,
            keyword: "IF",
            field: state.field,
            fieldOptions: bridgeConfig.conditionFieldOptions,
            operatorLabel: state.values.length === 1 ? "==" : "in",
            values: state.values,
            valueOptions: bridgeConfig.conditionValueOptions,
            valueInputLabel: bridgeConfig.conditionValueInputLabel,
        },
    };
};

const applySimplifiedLogicEdit = (
    config: StatementRuleConfiguration,
    edit: RuleSimplifiedLogicEdit,
    bridgeConfig: StatementRuleSimplifiedLogicBridgeConfig,
): StatementRuleConfiguration => {
    if (bridgeConfig.mode === "required_fields_for_condition") {
        if (edit.conditionId !== bridgeConfig.conditionId) return config;

        const characterization = characterizeRequiredFieldsCondition(config, bridgeConfig);
        if (characterization.status !== "complete") return config;

        const currentState = characterization.state;
        const conditionValues = edit.values
            ? normalizeWithOptions(edit.values, bridgeConfig.conditionValueOptions)
            : [currentState.conditionValue];
        const requiredFields = edit.requirementValues
            ? normalizeWithOptions(edit.requirementValues, bridgeConfig.requiredFieldOptions)
            : currentState.requiredFields;

        if (conditionValues.length === 0 || requiredFields.length === 0) return config;

        config.expression.expression = buildRequiredFieldsExpression(bridgeConfig, {
            conditionValue: conditionValues[0]!,
            requiredFields,
        });
        return config;
    }

    if (edit.conditionId !== bridgeConfig.conditionId) return config;

    const characterization = characterizeBlockedValuesCondition(config, bridgeConfig);
    if (characterization.status !== "complete") return config;

    const currentState = characterization.state;
    const fields = edit.field ? normalizeWithOptions([edit.field], bridgeConfig.conditionFieldOptions) : [currentState.field];
    const values = edit.values ? normalizeWithOptions(edit.values, bridgeConfig.conditionValueOptions) : currentState.values;

    if (fields.length === 0 || values.length === 0) return config;

    config.expression.expression = buildBlockedValuesExpression({
        field: fields[0]!,
        values,
    });
    return config;
};

export const createStatementRuleSimplifiedLogicBridge = (
    bridgeConfig: StatementRuleSimplifiedLogicBridgeConfig,
): StatementRuleSimplifiedLogicBridge => ({
    summaryPresenter: {
        renderSummary: (config, context) => {
            if (config.expression.valueType === ExpressionValueType.VALUE) {
                return resultValueSummary(config.expression.value);
            }

            if (bridgeConfig.mode === "required_fields_for_condition") {
                const characterization = characterizeRequiredFieldsCondition(config, bridgeConfig);
                return characterization.status === "complete"
                    ? createRuleSummaryPresentation({
                          status: "complete",
                          ast: buildRequiredFieldsSummaryAst(characterization.state, bridgeConfig, context),
                          editable: characterization.state.requiredFields.length > 0,
                          sources: ["native_appglue_expression", "adapter_routing"],
                      })
                    : unsupportedNativeSummary(config, characterization.warnings, bridgeConfig, context);
            }

            const characterization = characterizeBlockedValuesCondition(config, bridgeConfig);
            return characterization.status === "complete"
                ? createRuleSummaryPresentation({
                      status: "complete",
                      ast: buildBlockedValuesSummaryAst(characterization.state, bridgeConfig, context),
                      editable: characterization.state.values.length > 0,
                      sources: ["native_appglue_expression", "adapter_routing"],
                  })
                : unsupportedNativeSummary(config, characterization.warnings, bridgeConfig, context);
        },
    },
    simplifiedLogicEditor: {
        getEditorModel: (config) => getEditorModel(config, bridgeConfig),
        applyEdit: (config, edit) => applySimplifiedLogicEdit(config, edit, bridgeConfig),
    },
});
